
import React, { useState } from 'react';

interface MatchingWorksheetProps {
  content: any;
  onProgressUpdate: (newProgress: number, newScore?: number) => void;
  showPrintView: boolean;
}

const MatchingWorksheet: React.FC<MatchingWorksheetProps> = ({
  content,
  onProgressUpdate,
  showPrintView
}) => {
  const [userMatches, setUserMatches] = useState<{ [categoryName: string]: string[] }>({});
  const [availableItems, setAvailableItems] = useState<string[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  // Extract items and categories from content with better structure handling
  const items = React.useMemo(() => {
    if (content?.items && Array.isArray(content.items)) {
      // New structure: separate items array
      return content.items.map((item: any) => typeof item === 'string' ? item : item.name || item.text || String(item));
    }
    // Legacy structure: items with left/right pairs
    if (content?.left || content?.items) {
      const leftItems = content.left || content.items || [];
      return leftItems.map((item: any) => typeof item === 'string' ? item : item.left || item.name || String(item));
    }
    return [];
  }, [content]);

  const categories = React.useMemo(() => {
    if (content?.categories && Array.isArray(content.categories)) {
      // New structure: categories with correctItems
      return content.categories.map((cat: any) => ({
        name: typeof cat === 'string' ? cat : cat.name || String(cat),
        correctItems: cat.correctItems || []
      }));
    }
    // Legacy structure: groups/right items
    if (content?.groups || content?.right || content?.categories) {
      const groups = content.groups || content.right || content.categories || [];
      return groups.map((group: any) => ({
        name: typeof group === 'string' ? group : group.name || String(group),
        correctItems: []
      }));
    }
    return [];
  }, [content]);

  // Initialize available items when items change
  React.useEffect(() => {
    if (items.length > 0) {
      setAvailableItems([...items]);
    }
  }, [items]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: string) => {
    e.dataTransfer.setData("text/plain", item);
    setDraggedItem(item);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetCategory: string) => {
    e.preventDefault();
    const draggedItem = e.dataTransfer.getData("text/plain");
    
    if (draggedItem && !userMatches[targetCategory]?.includes(draggedItem)) {
      const newMatches = {
        ...userMatches,
        [targetCategory]: [...(userMatches[targetCategory] || []), draggedItem]
      };
      setUserMatches(newMatches);
      
      setAvailableItems(prev => prev.filter(item => item !== draggedItem));
      
      // Calculate progress
      const totalItems = items.length;
      const matchedItems = Object.values(newMatches).flat().length;
      const progress = (matchedItems / totalItems) * 100;
      
      // Calculate score based on correct matches
      let correctMatches = 0;
      categories.forEach(category => {
        const userCategoryMatches = newMatches[category.name] || [];
        userCategoryMatches.forEach(userItem => {
          if (category.correctItems.includes(userItem)) {
            correctMatches++;
          }
        });
      });
      
      const score = totalItems > 0 ? (correctMatches / totalItems) * 100 : 0;
      onProgressUpdate(progress, score);
    }
    setDraggedItem(null);
  };

  const removeFromCategory = (categoryName: string, item: string) => {
    const newMatches = {
      ...userMatches,
      [categoryName]: userMatches[categoryName]?.filter(i => i !== item) || []
    };
    setUserMatches(newMatches);
    setAvailableItems(prev => [...prev, item]);
    
    // Recalculate progress
    const totalItems = items.length;
    const matchedItems = Object.values(newMatches).flat().length;
    const progress = (matchedItems / totalItems) * 100;
    onProgressUpdate(progress);
  };

  if (showPrintView) {
    return (
      <div className="print:block">
        <div className="p-8">
          <h2 className="text-2xl font-bold mb-4">{content?.title || 'Matching Activity'}</h2>
          <p className="mb-6">{content?.instructions || 'Match the related items'}</p>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Items to Match:</h3>
              <div className="grid grid-cols-2 gap-2">
                {items.map((item: string, index: number) => (
                  <div key={index} className="border p-2 rounded">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Categories:</h3>
              <div className="grid grid-cols-1 gap-4">
                {categories.map((category: any, index: number) => (
                  <div key={index} className="border-2 border-dashed p-4 rounded">
                    <h4 className="font-semibold">{category.name}</h4>
                    <div className="mt-2 text-sm text-gray-500">
                      Items will be matched here
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('MatchingWorksheet Debug:', {
    content,
    items,
    categories,
    availableItems: availableItems.length,
    userMatches
  });

  if (items.length === 0 || categories.length === 0) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4 text-red-600">Loading Error</h2>
          <p className="text-gray-600 mb-4">Unable to load matching activity. Please try generating a new worksheet.</p>
          <div className="bg-gray-100 p-4 rounded text-sm text-left">
            <strong>Debug Info:</strong>
            <pre>{JSON.stringify({ items: items.length, categories: categories.length, content }, null, 2)}</pre>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6 text-center">
        <h2 className="text-2xl font-bold mb-2">{content?.title || 'Matching Activity'}</h2>
        <p className="text-gray-600">{content?.instructions || 'Drag items to match them with the correct categories'}</p>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Items */}
        <div className="lg:w-1/3 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
          <h3 className="text-lg font-semibold mb-4 text-blue-800">
            Items to Match ({availableItems.length} remaining)
          </h3>
          <div className="space-y-2 min-h-[300px]">
            {availableItems.map((item: string, index: number) => (
              <div
                key={`${item}-${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                className="bg-white p-3 rounded-lg cursor-move shadow-sm hover:shadow-md transition-all border-2 border-blue-100 hover:border-blue-300 hover:bg-blue-50"
              >
                {item}
              </div>
            ))}
            {availableItems.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                All items have been placed! 🎉
              </div>
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="lg:w-2/3 p-4">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">
            Match Categories ({categories.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((category: any, index: number) => {
              const categoryName = category.name;
              const userItems = userMatches[categoryName] || [];
              return (
                <div
                  key={index}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, categoryName)}
                  className="border-2 border-dashed border-gray-300 p-4 rounded-lg min-h-[150px] hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <h4 className="font-semibold mb-3 text-purple-700 border-b border-purple-200 pb-2">
                    {categoryName}
                  </h4>
                  <div className="space-y-2">
                    {userItems.length > 0 ? (
                      userItems.map((item, itemIndex) => {
                        const isCorrect = category.correctItems && category.correctItems.includes(item);
                        return (
                          <div 
                            key={itemIndex} 
                            className={`p-2 rounded border cursor-pointer transition-colors ${
                              isCorrect 
                                ? 'bg-green-100 border-green-300 text-green-800' 
                                : 'bg-red-100 border-red-300 text-red-800'
                            }`}
                            onClick={() => removeFromCategory(categoryName, item)}
                            title="Click to remove"
                          >
                            {item}
                            <span className="float-right text-xs">
                              {isCorrect ? '✓' : '✗'}
                            </span>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4 border-2 border-dashed border-gray-200 rounded">
                        Drop items here
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Progress indicator */}
      <div className="mt-6 bg-gray-100 rounded-lg p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-600">
            {Object.values(userMatches).flat().length} / {items.length} items placed
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${(Object.values(userMatches).flat().length / items.length) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default MatchingWorksheet;
